/**
 * Sync Status Indicator Component
 * Shows online/offline status, pending transactions, and preload status
 */

import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { useOfflineStatus } from '../useOfflineStatus';
import dataPreloader from '../dataPreloader';
import './SyncStatusIndicator.css';

const SyncStatusIndicator = ({ position = 'top-right', showDetails = false }) => {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    syncProgress,
    syncFailed,
    conflictsCount,
    triggerSync
  } = useOfflineStatus();
  
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadComplete, setPreloadComplete] = useState(false);

  // Check preload status
  useEffect(() => {
    const checkPreloadStatus = () => {
      setIsPreloading(dataPreloader.isPreloading);
      setPreloadComplete(dataPreloader.preloadComplete);
    };
    
    const interval = setInterval(checkPreloadStatus, 1000);
    checkPreloadStatus();
    
    return () => clearInterval(interval);
  }, []);

  const handleSyncClick = async () => {
    if (!isOnline || isSyncing) {
      console.log('Cannot sync:', !isOnline ? 'offline' : 'already syncing');
      return;
    }
    
    console.log('🔄 Manual sync triggered from status indicator');
    try {
      await triggerSync();
      console.log('✅ Manual sync completed');
    } catch (error) {
      console.error('❌ Manual sync failed:', error);
    }
  };

  const getStatusColor = () => {
    if (isPreloading) return 'preloading';
    if (!isOnline) return 'offline';
    if (isSyncing) return 'syncing';
    if (syncFailed && pendingCount > 0) return 'error'; // Only show error when sync FAILED AND has pending
    // Remove conflictsCount check - old conflicts shouldn't affect status
    return 'online';
  };

  const getStatusIcon = () => {
    if (isPreloading) return <Download size={20} className="spinning" />;
    if (!isOnline) return <WifiOff size={20} />;
    // Show percentage during sync if progress available
    if (isSyncing && syncProgress) {
      const percentage = Math.round((syncProgress.current / syncProgress.total) * 100);
      return (
        <div style={{ 
          fontSize: '11px', 
          fontWeight: 'bold', 
          color: '#1890ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px'
        }}>
          {percentage}%
        </div>
      );
    }
    if (isSyncing) return <RefreshCw size={20} className="spinning" />;
    if (syncFailed && pendingCount > 0) return <RefreshCw size={20} />; // Manual sync icon only if pending
    // Remove conflictsCount check - show WiFi icon when online and synced
    return <Wifi size={20} />;
  };

  const getTooltip = () => {
    if (isPreloading) return 'Loading data for offline use...';
    if (!isOnline) return `Offline Mode${preloadComplete ? ' (Ready)' : ''}`;
    if (isSyncing && syncProgress) {
      const percentage = Math.round((syncProgress.current / syncProgress.total) * 100);
      return `Syncing... ${percentage}% (${syncProgress.current}/${syncProgress.total})`;
    }
    if (isSyncing) return 'Syncing...';
    if (syncFailed) return `Auto-sync failed - ${pendingCount} pending - Click to retry`;
    return preloadComplete ? 'Online (Offline Ready)' : 'Online';
  };

  return (
    <div className={`sync-status-indicator ${position} ${getStatusColor()}`} title={getTooltip()}>
      {/* Icon Only - No Text */}
      <div 
        className="sync-status-main" 
        onClick={syncFailed ? handleSyncClick : undefined}
        style={{ 
          cursor: syncFailed && !isSyncing ? 'pointer' : 'default',
          position: 'relative'
        }}
      >
        <span className="sync-status-icon">
          {getStatusIcon()}
        </span>
        {/* Show pending count badge ONLY when sync failed */}
        {syncFailed && pendingCount > 0 && !isSyncing && (
          <span style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            background: '#ff4d4f',
            color: 'white',
            borderRadius: '12px',
            padding: '3px 7px',
            fontSize: '11px',
            fontWeight: 'bold',
            minWidth: '20px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
            border: '2px solid white'
          }}>
            {pendingCount}
          </span>
        )}
      </div>

      {/* Manual sync is now shown in the main icon when failed - no separate button needed */}

      {/* Show progress bar during sync */}
      {isSyncing && syncProgress && (
        <div className="sync-status-details">
          <div className="sync-progress">
            <div className="sync-progress-bar">
              <div 
                className="sync-progress-fill"
                style={{ 
                  width: `${(syncProgress.current / syncProgress.total) * 100}%` 
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatusIndicator;
